import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/compat/firestore';
import { DomSanitizer } from '@angular/platform-browser';
import { Browser } from '@capacitor/browser';
import { CredentialsService } from '@lib/core/authentication/credentials.service';
import { DeviceService } from '@lib/core/device.service';
import { Homepage } from '@lib/shared/models/home.model';
import { OpeningMessageService } from '@lib/shared/services/opening-message.service';
import { ToastService } from '@lib/shared/services/toast.service';
import { Observable, Subscription } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { addIcons } from 'ionicons';
import { logoFacebook, logoInstagram, logoTwitter, logoLinkedin } from 'ionicons/icons';
import { AngularFireStorage } from '@angular/fire/compat/storage';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss', '../../../../../lib/shared/styles/quill.scss'],
  standalone: false,
})
@Sentry.TraceClass({ name: 'HomeComponent' })
export class HomeComponent implements OnInit, OnDestroy {
  public home: Observable<Homepage>;
  private homeDoc: AngularFirestoreDocument<Homepage>;
  private homeSub: Subscription = new Subscription();
  private imageRequestId = 0;
  private currentSportsImagePath: string | null = null;
  private sportsImageObjectUrl: string | null = null;
  public sportsImageUrl: string | null = null;
  public isWeb: boolean = this.deviceService.isWeb;

  constructor(
    private afs: AngularFirestore,
    public sanitizer: DomSanitizer,
    public credentialsService: CredentialsService,
    private openingMessageService: OpeningMessageService,
    private deviceService: DeviceService,
    private toastService: ToastService,
    private afStorage: AngularFireStorage,
  ) {
    this.homeDoc = this.afs.doc<Homepage>(`homepage/homeContent`);
    this.home = this.homeDoc.valueChanges({ idField: 'id' });
    addIcons({ logoFacebook, logoInstagram, logoTwitter, logoLinkedin });
  }

  @Sentry.TraceMethod({ name: 'ngOnInit' })
  async ngOnInit() {
    this.homeSub = this.home.subscribe({
      next: (home) => void this.loadSportsImage(home?.sportsImage),
      error: (error) => {
        console.error(error);
        this.toastService.showDissmissableErrorToast('Unable to get the home content.');
      },
    });
    await this.openingMessageService.maybeShowAfterSignup(this.credentialsService.currentUserId);
  }

  @Sentry.TraceMethod({ name: 'ngOnDestroy' })
  ngOnDestroy() {
    this.imageRequestId++;
    this.homeSub.unsubscribe();
    this.revokeSportsImageUrl();
  }

  private async loadSportsImage(imageValue?: string): Promise<void> {
    const path = this.getSportsImagePath(imageValue);
    if (path === this.currentSportsImagePath) {
      return;
    }

    this.currentSportsImagePath = path;
    const requestId = ++this.imageRequestId;
    this.revokeSportsImageUrl();
    this.sportsImageUrl = null;
    if (!path) {
      return;
    }

    try {
      const blob = await this.afStorage.ref(path).getBlob();
      if (requestId !== this.imageRequestId) {
        return;
      }

      this.sportsImageObjectUrl = URL.createObjectURL(blob);
      this.sportsImageUrl = this.sportsImageObjectUrl;
    } catch (error) {
      if (requestId === this.imageRequestId) {
        console.error(error);
        this.toastService.showDissmissableErrorToast('Unable to load the homepage image.');
      }
    }
  }

  private getSportsImagePath(imageValue?: string): string | null {
    if (!imageValue) {
      return null;
    }

    let path = imageValue;
    if (/^https?:\/\//i.test(imageValue)) {
      try {
        const url = new URL(imageValue);
        const match =
          url.hostname === 'firebasestorage.googleapis.com' ? url.pathname.match(/^\/v0\/b\/[^/]+\/o\/(.+)$/) : null;
        if (!match) {
          return null;
        }
        path = decodeURIComponent(match[1]);
      } catch {
        return null;
      }
    }

    return path.startsWith('homepage-images/') ? path : null;
  }

  private revokeSportsImageUrl(): void {
    if (this.sportsImageObjectUrl) {
      URL.revokeObjectURL(this.sportsImageObjectUrl);
      this.sportsImageObjectUrl = null;
    }
  }

  async openPage(url: string): Promise<void> {
    try {
      this.deviceService.isWeb ? window.open(url, '_blank') : await Browser.open({ url });
    } catch (error) {
      console.error(error);
      this.toastService.showDissmissableErrorToast('Error opening page');
    }
  }
}
